import { memo } from "preact/compat";
import { useCallback, useState } from "preact/hooks";
import { useTranslation } from "react-i18next";
import { useAutoPlay, type AutoPlaySettings } from "../../hooks/useAutoPlay";
import { useNotification } from "../../shared/contexts";
import { GameState } from "../../shared/types/global.types";
import { Typography, Modal } from "../../shared/ui";
import { createPressHandlers } from "../../shared/utils/pressHandlers";
import { AutoPlayOptions } from "../AutoPlayOptions";
import { ActionButton } from "./ActionButton";
import { AutoCashOutSection } from "./AutoCashOutSection";
import { BetInputSection } from "./BetInputSection";
import { BonusIndicator } from "./BonusIndicator";
import css from "./ControlPanel.module.scss";
import { FreebetWarningModal } from "./FreebetWarningModal";
import { useBetState } from "./useBetState";
import { FREEBET_PROGRESS_REQUIRED } from "./useFreebets";

const DEFAULT_AUTOPLAY_SETTINGS: AutoPlaySettings = {
  numberOfRounds: 50,
  stopOnWin: false,
  stopOnWinAmount: 1,
  stopOnLoss: false,
  stopOnLossAmount: 1,
  stopOnBalance: false,
  stopOnBalanceAmount: 1,
};

interface ControlPanelProps {
  multiplier: number;
  gameState: GameState;
  slotId: 1 | 2;
}

export const ControlPanel = memo(function ControlPanel({
  multiplier,
  gameState,
  slotId,
}: ControlPanelProps) {
  const { showError } = useNotification();
  const { t } = useTranslation();
  const [isAutoPlayOptionsOpen, setIsAutoPlayOptionsOpen] = useState(false);
  const [apSettings, setApSettings] = useState<AutoPlaySettings>(
    DEFAULT_AUTOPLAY_SETTINGS
  );

  const { isAutoPlayActive, autoInfo, startAutoPlay, stopAutoPlay } =
    useAutoPlay(slotId);

  const {
    betAmount,
    isBetPlaced,
    isCashedOut,
    hasPendingBet,
    isFreebetWarningOpen,
    freebetProgress,
    freebets,
    isClimbing,
    isCountdown,
    betting,
    handleQuickBet,
    handleBetAmountChange,
    handlePlaceBet,
    handleCashOut,
    cancelPendingBet,
    confirmFreebetWarning,
    closeFreebetWarning,
    MAX_BET_AMOUNT,
  } = useBetState({ slotId, gameState });

  const handleCancelBet = () => {
    if (isAutoPlayActive) {
      stopAutoPlay();
      return;
    }
    // Cancel pending bet (awaiting next round)
    if (hasPendingBet) {
      cancelPendingBet();
      return;
    }
    // Cancel placed bet during countdown
    if (isCountdown && isBetPlaced) {
      betting.cancelBet({ slotId });
    }
  };

  const handleAutoPlayToggle = () => {
    if (isAutoPlayActive) {
      stopAutoPlay();
    } else {
      setIsAutoPlayOptionsOpen(true);
    }
  };

  const handleStartAutoPlay = (settings: AutoPlaySettings): boolean => {
    if (
      !settings.stopOnLoss &&
      !settings.stopOnWin &&
      !settings.stopOnBalance
    ) {
      showError(t("main.please_specify_stop_condition"));
      return false;
    }

    const ok = startAutoPlay({
      slotId,
      betAmount,
      rounds: settings.numberOfRounds ?? 0,
      stopOnLoss: settings.stopOnLoss ? settings.stopOnLossAmount : undefined,
      stopOnProfit: settings.stopOnWin ? settings.stopOnWinAmount : undefined,
      stopOnSingleWin: settings.stopOnBalance
        ? settings.stopOnBalanceAmount
        : undefined,
    });

    if (ok) {
      setIsAutoPlayOptionsOpen(false);
    }
    return ok ?? false;
  };

  const winAmount = betAmount * multiplier;
  const isAutoCashoutDisabled = isClimbing && betting.hasActiveBet;
  const closeAutoPlayModal = () => setIsAutoPlayOptionsOpen(false);

  const handleAutoCashoutToggle = useCallback(
    (enabled: boolean) => {
      const level = enabled ? betting.autoCashout.lastValid : 0;
      betting.setAutoCashOut(level);
    },
    [betting.autoCashout.lastValid, betting.setAutoCashOut]
  );

  return (
    <div className={css.controlPanel}>
      <div className={css.wrapper}>
        <div className={css.leftSection}>
          <BetInputSection
            betAmount={betAmount}
            maxBetAmount={MAX_BET_AMOUNT}
            isBlocked={isAutoPlayActive}
            onBetAmountChange={handleBetAmountChange}
            onQuickBet={handleQuickBet}
          />
          <button
            className={`${css.autoButton} ${isAutoPlayActive ? css.autoButtonActive : ""}`}
            {...createPressHandlers(handleAutoPlayToggle)}
          >
            <Typography variant="caption1" color="light-grey">
              {t("main.auto_play")}{" "}
              {isAutoPlayActive && autoInfo?.roundsRemaining
                ? `(${autoInfo.roundsRemaining})`
                : null}
            </Typography>
          </button>
        </div>

        <div className={css.rightSection}>
          <div className={css.centerSection}>
            <ActionButton
              isAutoPlayActive={isAutoPlayActive}
              isClimbing={isClimbing}
              isCountdown={isCountdown}
              isBetPlaced={isBetPlaced}
              isCashedOut={isCashedOut}
              hasPendingBet={hasPendingBet}
              winAmount={winAmount}
              onPlaceBet={handlePlaceBet}
              onCashOut={handleCashOut}
              onCancel={handleCancelBet}
            />
            <BonusIndicator
              currentProgress={
                freebets && freebets.length > 0
                  ? (freebetProgress?.requiredForFreebet ??
                    FREEBET_PROGRESS_REQUIRED)
                  : (freebetProgress?.currentProgress ?? 0)
              }
            />
          </div>
          <AutoCashOutSection
            autoCashout={betting.autoCashout}
            isDisabled={isAutoCashoutDisabled}
            onToggle={handleAutoCashoutToggle}
            onValueChange={betting.updateAutoCashoutUI}
            onCommit={betting.setAutoCashOut}
            showError={showError}
          />
        </div>
      </div>

      <Modal
        isOpen={isAutoPlayOptionsOpen}
        align="flex-end"
        onClose={closeAutoPlayModal}
      >
        <AutoPlayOptions
          onClose={closeAutoPlayModal}
          onStart={handleStartAutoPlay}
          settings={apSettings}
          onChange={(patch) => setApSettings((prev) => ({ ...prev, ...patch }))}
          onReset={() => setApSettings(DEFAULT_AUTOPLAY_SETTINGS)}
        />
      </Modal>
      <FreebetWarningModal
        isOpen={isFreebetWarningOpen}
        onClose={closeFreebetWarning}
        onConfirm={confirmFreebetWarning}
        freebets={freebets}
        freebetProgress={freebetProgress}
      />
    </div>
  );
});
